import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase.js';
import {
  formatMaintenanceFinEstimado,
  isSectionInMaintenance,
  maintenanceSectionLabel,
  parseMaintenanceConfig,
  PAGE_TO_MAINT_SECTION,
} from '../utils/maintenanceConfig.js';

/**
 * Lee `app_config.maintenance` y expone helpers para bloquear secciones en la app.
 * Los administradores no ven bloqueos (pueden probar la sección).
 */
export function useAppMaintenance(userId) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const tasks = [
        supabase.from('app_config').select('value').eq('key', 'maintenance').maybeSingle(),
      ];
      if (userId) {
        tasks.push(supabase.from('profiles').select('is_admin').eq('id', userId).maybeSingle());
      }
      const [maintRes, profRes] = await Promise.all(tasks);
      if (maintRes.error) throw maintRes.error;
      setCfg(parseMaintenanceConfig(maintRes.data?.value));
      setIsAdmin(!!profRes?.data?.is_admin);
    } catch (_) {
      setCfg(parseMaintenanceConfig(null));
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isSectionBlocked = useCallback(
    sectionKey => {
      if (isAdmin) return false;
      return isSectionInMaintenance(cfg, sectionKey);
    },
    [cfg, isAdmin]
  );

  const isPageBlocked = useCallback(
    page => {
      const sectionKey = PAGE_TO_MAINT_SECTION[page];
      if (!sectionKey) return false;
      return isSectionBlocked(sectionKey);
    },
    [isSectionBlocked]
  );

  const getBlockedPageMeta = useCallback(
    page => {
      const sectionKey = PAGE_TO_MAINT_SECTION[page];
      if (!sectionKey || !isSectionBlocked(sectionKey)) return null;
      return {
        sectionKey,
        sectionLabel: maintenanceSectionLabel(sectionKey),
        message: cfg?.message || '',
        finEstimadoText: formatMaintenanceFinEstimado(cfg?.fin_estimado),
      };
    },
    [cfg, isSectionBlocked]
  );

  return {
    cfg,
    loading,
    refresh,
    isAdmin,
    isSectionBlocked,
    isPageBlocked,
    getBlockedPageMeta,
    maintenanceMessage: cfg?.message || '',
  };
}
